// Components==============
import indexActivities from 'actions/activity/index';
import indexCategories from 'actions/category/index';
import indexFeedback from 'actions/feedback/index';
import indexNotification from 'actions/notification/index';
import indexPhases from 'actions/phase/index';
import indexProjectGroup from 'actions/project-group/index';
import indexProject from 'actions/project/index';
import indexTheme from 'actions/theme/index';
import indexTraining from 'actions/training/training/index';
import Layout from 'components/Layout/Layout';
import NavigationBlock from 'components/UI/NavigationBlock/NavigationBlock';
import NoContent from 'components/UI/NoContent/NoContent';
import ProjectProcess from 'components/UI/ProjectProcess/ProjectProcess';
import Summary from 'components/UI/Summary/Summary';
import { usePermission } from 'hooks/usePermission';
import { ProcessGroups, useProcessGroups } from 'hooks/useProcessGroups';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useRootModal } from 'hooks/useRootModal';
import { useTranslate } from 'hooks/useTranslate';
import { useMediaQ } from 'hooks/useMediaQ';
import {
  StudentListEntity,
  StudentListType,
  useStudentList,
} from 'pages/Workspace/ProjectDetails/components/useStudentList';
import React, {
  createContext,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { useHistory, useParams } from 'react-router';
import { UserMembership } from 'typings';
import { GlobalScanContext } from 'utilities/GlobalContext/GlobalScan';
import Communication from './components/Communication/Communication';
import Body from './components/Summary/Body/Body';
import TopLeft from './components/Summary/TopLeft/TopLeft';
import TopRight from './components/Summary/TopRight/TopRight';
import Top from './components/Top/Top';
import {
  ProjectDetailFunctions,
  useProjectDetailsFunctions,
} from './components/useProjectDetailsFunctions';
import './ProjectDetails.module.scss';
// import GlobalAlert from 'utilities/GlobalContext/GlobalAlert';

// =========================

type View = 'project.terms.process-view' | 'project.terms.communication';

export type ProjectDetailsContextType = {
  project?: ProjectProjectEntity;
  processGroups: ProcessGroups;
  setProcessGroups: React.Dispatch<React.SetStateAction<ProcessGroups>>;
  resetProcessGroups: () => void;
  theme?: ThemeEntity;
  isOpen: boolean;
  isEditable: boolean;
  setIsEditable: React.Dispatch<React.SetStateAction<boolean>>;
  scrollRef: React.RefObject<HTMLDivElement>;
  studentList: StudentListType;
  selectedStudent: string;
  setSelectedStudent: React.Dispatch<React.SetStateAction<string>>;
  currentStudent?: StudentListEntity;
  userId?: number;
  isMember?: boolean;
  saveKey: number;
  setSaveKey: React.Dispatch<React.SetStateAction<number>>;
  loading?: boolean;
  userMembership?: UserMembership;
  view: View;
  setView: React.Dispatch<React.SetStateAction<View>>;
  channelId?: string;
  deleteCondition: boolean;
  rootProject?: ProjectProjectEntity;
  hasStudents: boolean;
  sparks: any[];
  setSparks: React.Dispatch<React.SetStateAction<SparkEntity[]>>;
  isLocked?: boolean;
  isSaveable?: boolean;
};

export const ProjectDetailsContext = createContext(
  {} as ProjectDetailsContextType & ProjectDetailFunctions
);

export default function ProjectDetails() {
  const dispatch = useReduxAction();
  const translate = useTranslate();

  useEffect(() => {
    dispatch(indexTheme());
    dispatch(indexNotification());
    dispatch(indexProject());
    dispatch(indexProjectGroup());
    dispatch(indexTraining());
    dispatch(indexCategories());
    dispatch(indexActivities());
    dispatch(indexPhases());
    dispatch(indexFeedback());
  }, []);

  const loading = useRedux(
    (state) =>
      state.theme?.loading ||
      state.project?.loading ||
      state.projectGroup?.loading ||
      state.training?.loading ||
      state.category?.loading ||
      state.activity?.loading ||
      state.phase?.loading ||
      state.process?.loading ||
      state.feedback?.loading
  );

  const { projectId, channelId } = useParams() as {
    projectId: string;
    channelId?: string;
  };

  const [view, setView] = useState<View>(
    channelId ? 'project.terms.communication' : 'project.terms.process-view'
  );

  const [isOpen, setIsOpen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  const [isSaveable, setIsSaveable] = useState(false);

  const isTeacher = usePermission(['Docent', 'super-admin', 'Extern']);

  const userId = useRedux((state) => state.authentication?.user?.id);
  const project = useRedux((state) => state.project?.entities.byId[projectId]);
  const isLocked = !!project?.locked_for_assessment;
  const theme = useRedux((state) =>
    project?.theme_id
      ? state.theme?.entities.byId?.[project?.theme_id]
      : undefined
  );
  const [sparks, setSparks] = useState<SparkEntity[]>(project?.sparks || []);

  // student selector
  const studentList = useStudentList(theme);

  const defaultSelectedValue =
    (studentList?.find((student) => student?.projectId === project?.id)
      ?.name as string) || '';

  const [selectedStudent, setSelectedStudent] = useState(defaultSelectedValue);

  const currentStudent = studentList?.find(
    (student) => student.name === selectedStudent
  );

  const [counter, setCounter] = useState(0);
  const history = useHistory();

  useEffect(() => {
    if (studentList.length && !selectedStudent)
      setSelectedStudent(defaultSelectedValue);
  }, [studentList.length]);

  useEffect(() => {
    setCounter(1);
    if (counter > 0) {
      setIsEditable(false);
      history.push(`/werkplaats/project-details/${currentStudent?.projectId}`);
    }
  }, [selectedStudent]);
  //
  const images = theme?.attachments
    .filter((a) => a.attachment_type === 'image')
    .map((a) => JSON.parse(a.attachment_data).file_path);

  // we need this variable as reference in the case that you want to edit or delete your onw process on the page of one of your students
  const rootProject = useRedux((state) => {
    const myProcessProject = studentList.find(
      (s) => s.name === translate('project.terms.my-process')
    )?.projectId;

    return isTeacher && myProcessProject
      ? state.project?.entities.byId?.[myProcessProject]
      : project;
  });

  const scrollRef = useRef<HTMLDivElement>(null);

  const hasContent = project?.process ? project?.process.length > 0 : false;

  const userMembership: UserMembership | undefined = project?.members.find(
    (m) => m.user_id === userId
  )?.membership;

  const isMember =
    userMembership === 'member' ||
    userMembership === 'admin' ||
    userMembership === 'owner';

  const [saveKey, setSaveKey] = useState(0);

  useEffect(() => setSaveKey((prev) => prev + 1), [hasContent]);

  const membersLength = isTeacher
    ? project?.members.filter((m) => m.membership !== 'invited').length || 0
    : project?.members.filter(
        (m) => m.membership !== 'teacher' && m.membership !== 'invited'
      )?.length || 0;

  const deleteCondition =
    userMembership === 'owner' &&
    membersLength === 1 &&
    (isTeacher ? studentList.length === 0 : true);

  // is teacher and has other projects that it is supervising
  const hasStudents =
    isTeacher &&
    !!studentList.filter(
      (s) => s.name !== translate('project.terms.my-process')
    ).length;

  const {
    processGroups,
    setProcessGroups,
    resetProcessGroups,
    initializeProcessGroups,
  } = useProcessGroups(project, saveKey);

  const contextValues = {
    project,
    processGroups,
    resetProcessGroups,
    setProcessGroups,
    theme,
    isOpen,
    isEditable,
    setIsEditable,
    scrollRef,
    studentList,
    selectedStudent,
    setSelectedStudent,
    currentStudent,
    userId,
    isMember,
    saveKey,
    setSaveKey,
    loading,
    userMembership,
    view,
    setView,
    channelId,
    deleteCondition,
    rootProject,
    hasStudents,
    sparks,
    setSparks,
    isLocked,
    isSaveable,
  };

  const functions = useProjectDetailsFunctions(contextValues);

  const { setModal } = useRootModal();

  const [blockPathname, setBlockPathname] = useState<string | undefined>(
    undefined
  );

  const value = useContext(GlobalScanContext);

  useEffect(() => {
    if (!value.saveIsEditable) return;

    setIsEditable(true);
    value.setSaveIsEditable(false);
  }, [value.saveIsEditable]);

  const startPoint = processGroups?.find(
    (group) => group.id === 6 && group.process_type === 'App\\Phase'
  )?.description as StartPointType;

  const endPoint = processGroups?.find(
    (group) => group.id === 7 && group.process_type === 'App\\Phase'
  )?.description as EndPointType;

  const canSave =
    startPoint?.design_question && endPoint?.delivery_goals?.length;

  useEffect(() => {
    if (!isEditable) return;

    const unblock = history.block(({ pathname }) => {
      setModal(
        <NavigationBlock
          nextLocation={pathname}
          message={translate('course.confirm.discard-changes')}
          unblock={unblock}
          saveFunction={() => setBlockPathname(pathname)}
          showSave={!!canSave}
        />
      );

      return false;
    });

    return () => unblock();
  }, [isEditable, canSave]);

  const query = useMediaQ('min', 1024);
  const query2 = useMediaQ('max', 525);

  useEffect(() => {
    if (!blockPathname) return;

    return functions.handleProcessSave(undefined, blockPathname);
  }, [blockPathname]);

  return (
    <Layout grey>
      <ProjectDetailsContext.Provider
        value={{ ...contextValues, ...functions }}
      >
        <Summary
          type="project"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          topLeftContent={
            <TopLeft theme={theme} isOpen={isOpen} loading={loading} />
          }
          topRightContent={<TopRight />}
          bodyContent={<Body theme={theme} />}
          images={images}
          video={theme?.youtube_link}
        />
        <Top />
        <NoContent
          page={!query ? 'project-scan' : 'project'}
          loading={false}
          hasContent={processGroups.length > 0}
          returnNothing={!isMember}
          isTeacherMember={userMembership === 'teacher'}
          decreasedHeight={query2 ? 70 : 50}
          project_id={project?.id}
          handleClick={() => {
            initializeProcessGroups();
            setIsEditable(true);
          }}
        >
          {view === 'project.terms.process-view' ? (
            <ProjectProcess
              project={project}
              processGroups={processGroups}
              isEditable={isEditable}
              reference={scrollRef}
              setIsSaveable={setIsSaveable}
            />
          ) : (
            <Communication />
          )}
        </NoContent>
      </ProjectDetailsContext.Provider>
    </Layout>
  );
}
